import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import api from '../lib/api';
import { Calendar, Clock, Users, Star, ArrowRight } from 'lucide-react';

const Workshops = () => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    try {
      const res = await api.get('/bootcamps');
      setWorkshops(res.data || []);
    } catch (error) {
      console.error('Failed to fetch workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingWorkshops = workshops.filter(w => new Date(w.start_date) > new Date());

  return (
    <div className="min-h-screen bg-[#050505]">
      <SEO page="workshops" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF007F]/10 to-transparent" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-[#FF007F]">Workshops</span> & Masterclasses
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
              Intensive training sessions with industry professionals. Take your skills to the next level with our specialist workshops in dance, drama, and singing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Workshop Types */}
      <section className="py-12 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-[#FF007F]/20 to-transparent border-[#FF007F]/30">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
                  <Star className="w-8 h-8 text-[#FF007F]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Dance Workshops</h3>
                <p className="text-white/60 text-sm">
                  Ballet, Contemporary, Jazz, Street, and Commercial styles with professional choreographers.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-[#7000FF]/20 to-transparent border-[#7000FF]/30">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <Users className="w-8 h-8 text-[#7000FF]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Drama Workshops</h3>
                <p className="text-white/60 text-sm">
                  Acting technique, improvisation, audition skills, and scene work with industry actors.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-[#FF007F]/20 to-transparent border-[#FF007F]/30">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
                  <Star className="w-8 h-8 text-[#FF007F]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Vocal Workshops</h3>
                <p className="text-white/60 text-sm">
                  Musical theatre singing, vocal technique, harmony, and performance coaching.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Upcoming Workshops */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Upcoming Workshops
          </h2>
          
          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : upcomingWorkshops.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {upcomingWorkshops.map((workshop, index) => (
                <motion.div
                  key={workshop.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-white/5 border-white/10 hover:border-[#FF007F]/50 transition-colors overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-white">{workshop.title}</h3>
                          <p className="text-[#FF007F]">{workshop.instructor || 'TBA'}</p>
                        </div>
                        <span className="px-3 py-1 bg-[#FF007F]/20 text-[#FF007F] text-sm rounded-full">
                          {workshop.type || 'Workshop'}
                        </span>
                      </div>
                      <p className="text-white/60 text-sm mb-4">{workshop.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-white/50 mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(workshop.start_date).toLocaleDateString('en-GB', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {workshop.duration || '3 hours'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          Ages {workshop.min_age || 7}-{workshop.max_age || 18}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-white">
                          £{workshop.price || 45}
                        </span>
                        <Button className="bg-[#FF007F] hover:bg-[#D6006A]">
                          Book Now <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Upcoming Workshops</h3>
              <p className="text-white/60 mb-4">
                Check back soon for new workshop announcements, or sign up to our newsletter to be notified.
              </p>
              <Button 
                variant="outline" 
                className="border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white"
                onClick={() => window.location.href = '/contact'}
              >
                Get Notified
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Holiday Camps CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#7000FF]/20 to-[#FF007F]/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Looking for Holiday Camps?
          </h2>
          <p className="text-white/60 mb-6">
            Our holiday camps offer intensive training over multiple days. Perfect for school holidays!
          </p>
          <a 
            href="/bootcamps" 
            className="inline-flex items-center px-8 py-3 bg-[#7000FF] text-white font-bold rounded-lg hover:bg-[#5C00D6] transition-colors"
          >
            View Holiday Camps <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Workshops;
